import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { v4 as uuid } from 'uuid';
import { useSSE } from '../useSSE';
import {
  ensureChatExists,
  sendDraftGenerate,
  sendDraftQuestions,
  sendDraftTemplate,
  stopExecution,
} from '../../api/messages';
import { applyEditedDocument } from '../../office/ooxmlRedline';
import { downloadDocxFromS3 } from '../../api/s3';
import { truncate } from '../../lib/utils';
import {
  EMPTY_DRAFT_STATE,
  type DraftExperience,
  type DraftPhase,
  type DraftSessionState,
  type ProcessingMode,
  type QuestionAnswer,
  type TemplateSection,
} from './types';
import { clearDraftState, getDraftState, setDraftState } from './draftState';
import type { SseEvent } from '../types';

/**
 * Draft-mode state machine + SSE router.
 *
 * Phase transitions (driven by backend SSE events with `type: 'draft'`):
 *
 *   idle
 *     ──start()──▶ prompt
 *                    └─sendPrompt(brief)─▶ template_loading
 *                                             └─(SSE template_generation)─▶ template_review
 *                                                                              └─confirmTemplate()─▶ questions_loading
 *                                                                                                     └─(SSE questions_generation)─▶ qna
 *                                                                                                                                      └─submitAnswers()─▶ draft_loading
 *                                                                                                                                                            └─(SSE draft_generation + s3_key)─▶ complete
 *                                                                                                                                                                                                  └─(apply DOCX to Word)─▶ idle
 *
 * Each phase persists to `Office.context.document.settings` so closing Word
 * mid-QnA doesn't lose user progress (they resume when they reopen the doc).
 *
 * Stop / cancel at any in-flight phase calls `/api/v1/chat/stop` and leaves
 * the overlay on the same phase (matches the web app). User can press Next
 * again to retry.
 */
export interface UseDraftState {
  phase: DraftPhase;
  template: TemplateSection[];
  questions: QuestionAnswer[];
  error: string | null;
  chatId: string | null;
  /** F1 — currently selected draft experience (Guided/Auto). Persisted. */
  draftExperience: DraftExperience;
  /** F3 — current processing mode (Concise/Detailed). Persisted. */
  processingMode: ProcessingMode;

  /** Enter the draft flow (create chat on first entry). Safe to call repeatedly. */
  start: () => Promise<void>;
  /** F1 — change the experience without altering phase. */
  setDraftExperience: (next: DraftExperience) => void;
  /** F3 — change the processing mode without altering phase. */
  setProcessingMode: (next: ProcessingMode) => void;
  /**
   * Adopt an existing chat_id into the draft session (no new UUID created).
   * Used when the user enters draft mode with an already-loaded chat of
   * chat_type='draft' — e.g. after a fork-from-research or after clicking a
   * draft thread in the History sidebar. Subsequent sendPrompt /
   * confirmTemplate / submitAnswers calls write to this same chat.
   */
  attach: (chatId: string) => Promise<void>;
  /**
   * User wrote a brief in the prompt phase → fires generate_template.
   * `alreadyExists` tells us whether the chat row is already server-side
   * (resumed from history / forked). Matches web's gate of only calling
   * createChat when messages.length === 0 on mount.
   * When `draftExperience === 'instant'` the phase advances to
   * `instant_loading` instead of `template_loading` and intermediate
   * SSE template/questions events are ignored (the agent skips QnA).
   */
  sendPrompt: (brief: string, userId: string, alreadyExists?: boolean) => Promise<void>;
  /** Persist template edits (reorder, rename) without advancing. */
  updateTemplate: (template: TemplateSection[]) => void;
  /** Advance from template_review → questions_loading. */
  confirmTemplate: (userId: string) => Promise<void>;
  /** Update a single answer in the qna phase. */
  setAnswer: (questionId: string, answer: string) => void;
  /** Advance from qna → draft_loading. */
  submitAnswers: (userId: string) => Promise<void>;
  /** Cancel in-flight SSE phase; keeps current overlay visible. */
  stop: () => Promise<void>;
  /** Wipe draft state (after completion or explicit discard). */
  reset: () => Promise<void>;
}

export function useDraft(): UseDraftState {
  const initial = typeof Office !== 'undefined' ? getDraftState() : EMPTY_DRAFT_STATE;

  const [phase, setPhase] = useState<DraftPhase>(initial.phase);
  const [template, setTemplate] = useState<TemplateSection[]>(initial.template);
  const [questions, setQuestions] = useState<QuestionAnswer[]>(initial.questions);
  const [error, setError] = useState<string | null>(null);
  const [chatId, setChatId] = useState<string | null>(initial.chatId);
  const [draftExperience, setDraftExperienceState] = useState<DraftExperience>(
    initial.draftExperience,
  );
  const [processingMode, setProcessingModeState] = useState<ProcessingMode>(
    initial.processingMode,
  );

  // Refs so the SSE handler reads the current state without re-subscribing.
  const phaseRef = useRef(phase);
  phaseRef.current = phase;
  const chatIdRef = useRef<string | null>(chatId);
  chatIdRef.current = chatId;
  const experienceRef = useRef<DraftExperience>(draftExperience);
  experienceRef.current = draftExperience;
  const modeRef = useRef<ProcessingMode>(processingMode);
  modeRef.current = processingMode;

  const persist = useCallback(async (patch: Partial<DraftSessionState>) => {
    if (typeof Office === 'undefined') return;
    await setDraftState(patch).catch(() => {
      /* storage full / settings locked — non-fatal */
    });
  }, []);

  // ----- State transitions -----

  const start = useCallback(async () => {
    if (chatIdRef.current) return; // already inside a draft
    const id = uuid();
    const featureId = uuid();
    setChatId(id);
    setPhase('prompt');
    setTemplate([]);
    setQuestions([]);
    setError(null);
    await persist({ chatId: id, featureId, phase: 'prompt', template: [], questions: [] });
  }, [persist]);

  const setDraftExperience = useCallback(
    (next: DraftExperience) => {
      setDraftExperienceState(next);
      void persist({ draftExperience: next });
      // F1 ↔ F3 constraint: Instant always clamps to Concise.
      if (next === 'instant' && modeRef.current !== 'speed') {
        setProcessingModeState('speed');
        void persist({ processingMode: 'speed' });
      }
    },
    [persist],
  );

  const setProcessingMode = useCallback(
    (next: ProcessingMode) => {
      // Guard the F1 ↔ F3 constraint: Instant forces Concise; ignore upgrades.
      if (experienceRef.current === 'instant' && next !== 'speed') return;
      setProcessingModeState(next);
      void persist({ processingMode: next });
    },
    [persist],
  );

  const attach = useCallback(
    async (incomingChatId: string) => {
      if (!incomingChatId) return;
      if (chatIdRef.current === incomingChatId) return;
      const state = getDraftState();
      const featureId = state.featureId ?? uuid();
      setChatId(incomingChatId);
      // Keep current phase/template/questions — resume logic elsewhere may
      // hydrate them from message history. Only set to 'prompt' if idle.
      if (phaseRef.current === 'idle') {
        setPhase('prompt');
      }
      setError(null);
      await persist({ chatId: incomingChatId, featureId });
    },
    [persist],
  );

  const sendPrompt = useCallback(
    async (brief: string, userId: string, alreadyExists?: boolean) => {
      const trimmed = brief.trim();
      if (!trimmed) return;
      // chat_id === feature_id === draft_id — matches the web UI convention.
      const id = chatIdRef.current ?? uuid();
      const name = truncate(trimmed, 60);
      const experience = experienceRef.current;
      const nextPhase: DraftPhase =
        experience === 'instant' ? 'instant_loading' : 'template_loading';
      try {
        setError(null);
        setPhase(nextPhase);
        setChatId(id);
        await persist({
          chatId: id,
          featureId: id, // same UUID
          phase: nextPhase,
          prompt: trimmed,
          draftExperience: experience,
        });
        // Only create the chat row when the thread is brand-new. Matches web's
        // gate (createChat dispatched only when messages.length === 0). If the
        // thread was loaded from history / is a fork-target, it already exists
        // server-side — firing POST /chat would 500 on the deployed backend
        // that lacks ON CONFLICT DO NOTHING.
        if (!alreadyExists) {
          await ensureChatExists({
            chatId: id,
            chatType: 'draft',
            name,
            metadata: { status: 'TemplateGenerated', source: 'word_addon' },
          });
        }
        // No separate POST /api/v1/draft: the generate_template message's
        // `data: [{type:'draft', operation:'insert'}]` creates the draft row
        // server-side, exactly like web does.
        const mode = modeRef.current;
        await sendDraftTemplate({
          userId,
          chatId: id,
          prompt: trimmed,
          name,
          // Only send `draft_experience` when it differs from the default
          // so existing telemetry/payload baselines stay byte-stable.
          draftExperience: experience === 'instant' ? 'instant' : undefined,
          // F3 — only send `mode` when 'accuracy'; 'speed' is backend default.
          mode: mode === 'accuracy' ? 'accuracy' : undefined,
        });
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to generate template');
        setPhase('prompt');
        await persist({ phase: 'prompt' });
        // eslint-disable-next-line no-console
        console.warn('[useDraft] sendPrompt failed', err);
      }
    },
    [persist],
  );

  const updateTemplate = useCallback(
    (next: TemplateSection[]) => {
      setTemplate(next);
      void persist({ template: next });
    },
    [persist],
  );

  const confirmTemplate = useCallback(
    async (userId: string) => {
      const id = chatIdRef.current;
      if (!id) return;
      try {
        setError(null);
        setPhase('questions_loading');
        await persist({ phase: 'questions_loading' });
        const mode = modeRef.current;
        await sendDraftQuestions({
          userId,
          chatId: id,
          template,
          // F3 — only send when non-default.
          mode: mode === 'accuracy' ? 'accuracy' : undefined,
        });
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to request questions');
        setPhase('template_review');
        await persist({ phase: 'template_review' });
        // eslint-disable-next-line no-console
        console.warn('[useDraft] confirmTemplate failed', err);
      }
    },
    [persist, template],
  );

  const setAnswer = useCallback(
    (questionId: string, answer: string) => {
      setQuestions((qs) => {
        const next = qs.map((q) => (q.id === questionId ? { ...q, answer } : q));
        void persist({ questions: next });
        return next;
      });
    },
    [persist],
  );

  const submitAnswers = useCallback(
    async (userId: string) => {
      const id = chatIdRef.current;
      if (!id) return;
      try {
        setError(null);
        setPhase('draft_loading');
        await persist({ phase: 'draft_loading' });
        const mode = modeRef.current;
        await sendDraftGenerate({
          userId,
          chatId: id,
          template,
          questions,
          // F3 — only send when non-default.
          mode: mode === 'accuracy' ? 'accuracy' : undefined,
        });
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to generate draft');
        setPhase('qna');
        await persist({ phase: 'qna' });
        // eslint-disable-next-line no-console
        console.warn('[useDraft] submitAnswers failed', err);
      }
    },
    [persist, questions, template],
  );

  const stop = useCallback(async () => {
    const id = chatIdRef.current;
    if (!id) return;
    await stopExecution(id);
  }, []);

  const reset = useCallback(async () => {
    setPhase('idle');
    setTemplate([]);
    setQuestions([]);
    setChatId(null);
    setError(null);
    if (typeof Office !== 'undefined') await clearDraftState();
  }, []);

  // ----- SSE routing -----

  const processSseEvent = useCallback(
    (event: SseEvent) => {
      if (event.type !== 'draft' && event.type !== 'draft_chat') return;
      if (!chatIdRef.current || event.thread_id !== chatIdRef.current) return;

      if (event.status === 'failed' || event.status === 'error') {
        setError(event.response || 'Draft generation failed');
        return;
      }

      // F1 — In instant mode the agent may still emit intermediate
      // template / questions events. Ignore them and wait for the final
      // draft_generation event so the overlay stays on `instant_loading`
      // and we don't flash the template-review UI.
      const isInstant = phaseRef.current === 'instant_loading';

      // Template arrived
      if (
        !isInstant &&
        event.sub_type === 'template_generation' &&
        event.metadata?.template
      ) {
        const incoming = normalizeTemplate(event.metadata.template);
        setTemplate(incoming);
        setPhase('template_review');
        void persist({ template: incoming, phase: 'template_review' });
        return;
      }

      // Questions arrived
      if (
        !isInstant &&
        event.sub_type === 'questions_generation' &&
        event.metadata?.qna
      ) {
        const incoming = normalizeQuestions(event.metadata.qna);
        setQuestions(incoming);
        setPhase('qna');
        void persist({ questions: incoming, phase: 'qna' });
        return;
      }

      // Draft arrived — download + apply to Word
      if (event.sub_type === 'draft_generation' && event.metadata?.s3_key) {
        const s3Key = event.metadata.s3_key;
        setPhase('draft_loading'); // keep spinner up until apply succeeds
        void persist({ phase: 'draft_loading', draftS3Key: s3Key });
        applyDraftDocx(s3Key)
          .then(async () => {
            setPhase('complete');
            await persist({ phase: 'complete' });
          })
          .catch((err) => {
            setError(err instanceof Error ? err.message : 'Failed to apply draft to document');
            setPhase('qna');
            void persist({ phase: 'qna' });
          });
        return;
      }
    },
    [persist],
  );

  useSSE({ onEvent: processSseEvent });

  // If the add-in boots into a non-idle persisted state, keep the phase as
  // loaded (so QnA answers aren't lost across Word restarts).
  useEffect(() => {
    if (initial.phase !== 'idle') {
      setPhase(initial.phase);
      setTemplate(initial.template);
      setQuestions(initial.questions);
      setChatId(initial.chatId);
    }
    // Always restore the experience + mode choices — independent of phase.
    setDraftExperienceState(initial.draftExperience);
    setProcessingModeState(initial.processingMode);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return useMemo(
    () => ({
      phase,
      template,
      questions,
      error,
      chatId,
      draftExperience,
      processingMode,
      start,
      setDraftExperience,
      setProcessingMode,
      attach,
      sendPrompt,
      updateTemplate,
      confirmTemplate,
      setAnswer,
      submitAnswers,
      stop,
      reset,
    }),
    [
      phase,
      template,
      questions,
      error,
      chatId,
      draftExperience,
      processingMode,
      start,
      setDraftExperience,
      setProcessingMode,
      attach,
      sendPrompt,
      updateTemplate,
      confirmTemplate,
      setAnswer,
      submitAnswers,
      stop,
      reset,
    ],
  );
}

// ----- pure helpers -----

/**
 * Convert the AI agent's wire format into our internal shape.
 * Server sends (from AI/tools/draft/template_agent.py):
 *   { section_number, section_header, description, must_include[],
 *     expected_depth, generation_guidance, complexity, section_subheaders[] }
 *
 * We preserve every field so the collapsible "Why this section" panel can
 * render the full context. Nothing is discarded.
 */
function normalizeTemplate(raw: unknown): TemplateSection[] {
  const sections = Array.isArray((raw as { sections?: unknown[] }).sections)
    ? (raw as { sections: unknown[] }).sections
    : Array.isArray(raw)
      ? (raw as unknown[])
      : [];
  return sections.map((s, i) => {
    const r = (s ?? {}) as Record<string, unknown>;
    const number = asString(r.section_number ?? r.number);
    const header = asString(r.section_header ?? r.header ?? r.title) || `Section ${i + 1}`;
    const depth = asString(r.expected_depth ?? r.expectedDepth);
    const complexity = asString(r.complexity);
    const subheaders = normalizeSubheaders(r.section_subheaders ?? r.subheaders, i);
    return {
      id: asString(r.id) || `tpl-${i}-${number || i + 1}`,
      number,
      header,
      description: asString(r.description) || undefined,
      mustInclude: Array.isArray(r.must_include)
        ? (r.must_include as unknown[]).map((x) => String(x))
        : Array.isArray(r.mustInclude)
          ? (r.mustInclude as unknown[]).map((x) => String(x))
          : undefined,
      guidance: asString(r.generation_guidance ?? r.guidance) || undefined,
      expectedDepth: depth === 'exhaustive' || depth === 'standard' || depth === 'brief' ? depth : undefined,
      complexity: complexity === 'standard' || complexity === 'complex' ? complexity : undefined,
      subheaders,
    };
  });
}

function normalizeSubheaders(raw: unknown, parentIdx: number): TemplateSection['subheaders'] {
  if (!Array.isArray(raw)) return [];
  return (raw as unknown[]).map((s, i) => {
    // Server sends objects with {section_number, title, description}. Old shape
    // used bare strings; accept both.
    if (typeof s === 'string') {
      return { id: `sh-${parentIdx}-${i}`, title: s };
    }
    const r = (s ?? {}) as Record<string, unknown>;
    const number = asString(r.section_number ?? r.number);
    return {
      id: asString(r.id) || `sh-${parentIdx}-${i}-${number || i + 1}`,
      number,
      title: asString(r.title ?? r.header) || `Subsection ${i + 1}`,
      description: asString(r.description) || undefined,
    };
  });
}

function asString(v: unknown): string {
  return typeof v === 'string' ? v : '';
}

function normalizeQuestions(raw: unknown): QuestionAnswer[] {
  const list = Array.isArray((raw as { questions?: unknown[] }).questions)
    ? (raw as { questions: unknown[] }).questions
    : Array.isArray(raw)
      ? (raw as unknown[])
      : [];
  return list.map((q, i) => {
    const r = (q ?? {}) as Record<string, unknown>;
    return {
      id: String(r.id ?? `q-${i}`),
      question: String(r.question ?? r.text ?? `Question ${i + 1}`),
      answer: typeof r.answer === 'string' ? (r.answer as string) : null,
    };
  });
}

async function applyDraftDocx(s3Key: string): Promise<void> {
  const buf = await downloadDocxFromS3(s3Key);
  const bytes = new Uint8Array(buf);
  // For a generated draft we don't have a meaningful "current" doc to diff
  // against (the user started from scratch), so pass undefined and let
  // applyEditedDocument take the full-replace path.
  await applyEditedDocument(bytes, undefined);
}
